import type { Styles } from '@adornis/ass/style.js';
import { A } from '@adornis/base/env-info.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Certificate } from '@adornis/buchungsheld-db/entities/certificate.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Setting } from '@adornis/buchungsheld-db/entities/settings.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import '@adornis/forms/x-input.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, firstValueFrom, switchMap, timer } from 'rxjs';
import '../../../../components/bh-participation-dataview.js';
import { BHPromptProductParticipationQR } from '../../../../prompts/bh-prompt-product-participation-qr.js';
import { BHPromptParticipationModify } from '../../../../prompts/participation-modify/bh-prompt-participation-modify.js';
import './bh-page-admin-einrichtung-counseling-slotview.js';

/**
 * @element bh-page-admin-einrichtung-counseling-edit
 */
@customElement('bh-page-admin-einrichtung-counseling-edit')
export class BHPageAdminEinrichtungCounselingEdit extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID?: string;
  @property({ attribute: 'counseling-id' }) counselingID = new RXController<string | null>(this, null);

  @state() private readonly _counseling = new RXController(
    this,
    this.counselingID.observable.pipe(
      filter(Boolean),
      switchMap(id =>
        Einzelberatung.subscribeByID<Einzelberatung>(id)({
          name: 1,
          _id: 1,
          _class: 1,
          institutionID: 1,
          settings: selectionSet(() => Setting, 2),
          slots: { _id: 1, start: 1, end: 1, beraterID: 1, isBooked: 1, isNonBlocking: 1 },
        }),
      ),
    ),
  );

  override render() {
    if (!this.institutionID) {
      return html` <x-loading-state></x-loading-state> `;
    }

    return html` ${this._content()} `;
  }

  private _content() {
    if (this.counselingID.isLoading || this._counseling.isLoading || !this._counseling.value) {
      return html` <x-loading-state></x-loading-state> `;
    }

    return html`
      <x-flex padding="md" space="xs">
        <x-flex
          horizontal
          crossaxis-center
          space="sm"
          padding="xxs"
          ${css({ color: this.colors.accent, cursor: 'pointer' })}
          @click=${() =>
            goTo(`/admin/institution-details/${this.institutionID}/counselings/edit/${this.counselingID.value}`)}
        >
          <x-icon> article </x-icon>
          <x-text>${this._translation.translate('counseling', 'edit')}</x-text>
        </x-flex>

        <bh-icon-link
          icon="article"
          text=${this._translation.translate('workshop', 'download_timeslots')}
          @click=${() => this.downloadSlotPdf()}
        ></bh-icon-link>

        <x-flex
          horizontal
          crossaxis-center
          space="sm"
          padding="xxs"
          ${css({ color: this.colors.accent, cursor: 'pointer' })}
          @click=${() =>
            BHPromptProductParticipationQR.showPopup({ props: { productID: this.counselingID.value ?? '' } })}
        >
          <x-icon> qr_code_2 </x-icon>
          <x-text> ${this._translation.translate('workshop', 'generate_participation_qrcode')}... </x-text>
        </x-flex>

        <x-flex
          horizontal
          crossaxis-center
          space="sm"
          padding="xxs"
          ${css({ color: this.colors.accent, cursor: 'pointer' })}
          @click=${() => this.sendCertificates().catch(this.e)}
        >
          <x-icon> approval </x-icon>
          <x-text> ${this._translation.translate('workshop', 'send_certificates')}... </x-text>
        </x-flex>

        <x-flex
          horizontal
          crossaxis-center
          space="sm"
          padding="xxs"
          ${css({ color: this.colors.accent, cursor: 'pointer' })}
          @click=${async () => {
            const product = this._counseling.value!;
            await BHPromptParticipationModify.add(product._id, Einzelberatung._class, product.institutionID);
          }}
        >
          <x-icon> add </x-icon>
          <x-text> ${this._translation.translate('PARTICIPATION_DATAVIEW', 'addParticipant')}... </x-text>
        </x-flex>
      </x-flex>

      <x-hr> </x-hr>

      ${this._importExportSection(this._counseling.value)}

      <x-hr> </x-hr>

      <bh-participation-dataview .product=${this._counseling.value}></bh-participation-dataview>
    `;
  }

  private _importExportSection(einzelberatung: Einzelberatung) {
    return html`
      <x-flex space="md">
        <x-h3> Import / Export </x-h3>
        <x-flex horizontal space="md">
          <x-button
            mode="outline"
            icon="download"
            @click=${async () => {
              const workshopID = einzelberatung._id;
              if (!workshopID) return;

              try {
                await XDialog.waitFor(
                  Promise.all([
                    Einzelberatung.exportEinzelberatungParticipantsCSV(einzelberatung._id)().then(fileName =>
                      downloadLink(A.absoluteUrl(`/__export/download/${fileName}`), 'einzelberatung-teilnehmer.csv'),
                    ),
                    firstValueFrom(timer(500)),
                  ]),
                );
              } catch (e) {
                if ((e as any).message === 'file is empty') {
                  await XDialog.alert(this._translation.translate('imp_exp', 'no_data_found'));
                } else {
                  await XDialog.alert('Es gab ein Problem beim Exportieren der Workshops');
                }
              }
            }}
          >
            ${this._translation.translate('imp_exp', 'exp_participants')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  async sendCertificates() {
    const workshop = this._counseling.value;
    if (!workshop) throw new Error('counseling not loaded yet');

    // ask before sending mails
    const question = this._translation.translate('workshop', 'question_send_certificates');
    const reallySentCertificates = await TrDialog.confirm(question);
    if (!reallySentCertificates) return;

    // send mails
    await TrDialog.waitFor(Certificate.sendCertificateToProductParticipantsWhichHaveParticipated(workshop._id)());
    void XSnackbar.show(this._translation.translate('workshop', 'successfully_sent_certificates'));
  }

  private async downloadSlotPdf() {
    const counselingID = this.counselingID.value;
    if (!counselingID) return;

    await XDialog.waitFor(
      (async () => {
        const link = await Einzelberatung.getBeraterSlotPdfLink(counselingID)();
        window.open(link, '_blank');
      })(),
    );
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '.hover-text-button:hover': {
          background: 'red',
        },
      },
    ];
  }
}
